import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Alert, Box, Button, CircularProgress, FormControl, Input, Link, MenuItem, Select, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import Layout from '../layouts/application';
import { ThemeProvider } from '@mui/material/styles';
import { themeAttuned } from '../../constants/theme';
import { ConfirmDialogProvider } from '../../components/modals/confirm_dialog';
import FilterBarBespoke2026 from '../../components/survey_collections/pulse_analysis_v2/filter_bar_bespoke_2026';
import {
  BORDERLESS_SELECT_SX,
  PULSE_V2_NEUTRAL_SURFACE_BG,
  SelectChevronIcon,
} from '../../components/survey_collections/pulse_analysis_v2/control_language_styles';
import LikertSection, { DEFAULT_THEME_KEY, DEFAULT_VIEW_MODE } from '../../components/survey_collections/pulse_analysis_v2/likert_section';
import OpenTextSection from '../../components/survey_collections/pulse_analysis_v2/open_text_section';
import { fetchPulseAnalysisV2Data, exportPulseOpenTextCsv } from '../../apis/survey_collections/pulse_analysis_v2_api';

const normalizeFilterValue = (value) => {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  const stringValue = `${value}`;
  return stringValue === 'null' ? null : stringValue;
};

const normalizeFilterArray = (value) => {
  const values = Array.isArray(value) ? value : [value];

  const normalized = values
    .map((item) => normalizeFilterValue(item))
    .filter(Boolean)
    .filter((item) => item.toLowerCase() !== 'all');

  return [...new Set(normalized)].sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }));
};

const normalizeGroupBy = (value) => {
  const normalized = `${value || ''}`.trim().toLowerCase();
  if (normalized === 'school' || normalized === 'role') {
    return normalized;
  }
  return 'none';
};

const formatFilters = (filters = {}) => {
  const source = filters || {};
  return {
    survey_id: normalizeFilterValue(source.survey_id),
    school: normalizeFilterArray(source.school),
    role: normalizeFilterArray(source.role),
    group_by: normalizeGroupBy(source.group_by),
  };
};

const areFiltersEqual = (left, right) => JSON.stringify(formatFilters(left)) === JSON.stringify(formatFilters(right));

const mergeFilters = (base, updates) => {
  const normalized = formatFilters({ ...base, ...updates });
  return areFiltersEqual(base, normalized) ? base : normalized;
};

const getInitialFilters = (reportData, reportFilters) => ({
  survey_id: normalizeFilterValue(reportData?.filters?.survey_id || reportFilters?.survey_id),
  school: normalizeFilterArray(reportData?.filters?.school ?? reportFilters?.school),
  role: normalizeFilterArray(reportData?.filters?.role ?? reportFilters?.role),
  group_by: normalizeGroupBy(reportData?.filters?.group_by || reportFilters?.group_by),
});

const surveyPeriodSelectStyles = {
  ...BORDERLESS_SELECT_SX,
  '& .MuiInputBase-root': {
    ...BORDERLESS_SELECT_SX['& .MuiInputBase-root'],
    borderRadius: '10px',
    px: 1.2,
    py: 0.45,
    backgroundColor: 'transparent',
    transition: 'background-color 140ms ease',
    '&:hover': {
      backgroundColor: PULSE_V2_NEUTRAL_SURFACE_BG,
    },
  },
  '& .MuiSelect-select': {
    ...BORDERLESS_SELECT_SX['& .MuiSelect-select'],
    paddingTop: '2px',
    paddingBottom: '2px',
    paddingRight: '34px !important',
  },
  '& .MuiSelect-icon': {
    ...BORDERLESS_SELECT_SX['& .MuiSelect-icon'],
    right: 8,
  },
};

const getStickyTopOffset = () => {
  if (typeof document === 'undefined') {
    return 60;
  }

  let offset = 60;

  if (document.querySelector('.staging-banner')) {
    offset += 50;
  }

  if (document.querySelector('.mimic-banner')) {
    offset += 50;
  }

  return offset;
};

const PulseAnalysisV2Container = ({ api_url, export_open_text_url, links, permission_props, report_filters, initial_report_data }) => {
  const initialFilters = getInitialFilters(initial_report_data, report_filters);
  const [reportData, setReportData] = useState(initial_report_data || null);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [draftFilters, setDraftFilters] = useState(initialFilters);
  const [lastFetchedFilters, setLastFetchedFilters] = useState(initial_report_data ? formatFilters(initialFilters) : null);
  const [isLoading, setIsLoading] = useState(!initial_report_data);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [likertViewMode, setLikertViewMode] = useState(DEFAULT_VIEW_MODE);
  const [sortOrder, setSortOrder] = useState('default');
  const [themeKey, setThemeKey] = useState(DEFAULT_THEME_KEY);
  const isSuperadmin = Boolean(permission_props?.is_superadmin);
  const [stickyTopOffset, setStickyTopOffset] = useState(60);
  const [isFilterBarStuck, setIsFilterBarStuck] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const filterBarSentinelRef = useRef(null);
  const latestRequestIdRef = useRef(0);

  const sections = useMemo(
    () => ({
      likert: reportData?.sections?.likert || [],
      likert_grouped: reportData?.sections?.likert_grouped || null,
      open_text: reportData?.sections?.open_text || [],
    }),
    [reportData]
  );

  const fetchReportData = useCallback(
    async (requestedFilters, isInitialLoad = false) => {
      const requestId = latestRequestIdRef.current + 1;
      latestRequestIdRef.current = requestId;

      try {
        setError(null);
        if (isInitialLoad) {
          setIsLoading(true);
        } else {
          setIsRefreshing(true);
        }
        const response = await fetchPulseAnalysisV2Data(api_url, requestedFilters);
        if (requestId !== latestRequestIdRef.current) {
          return;
        }
        setReportData(response);
        const normalizedRequestedFilters = formatFilters(requestedFilters);
        setAppliedFilters(normalizedRequestedFilters);
        setDraftFilters(normalizedRequestedFilters);
      } catch (fetchError) {
        if (requestId !== latestRequestIdRef.current) {
          return;
        }
        setError(fetchError.message || 'Unable to load pulse analysis data.');
      } finally {
        if (requestId === latestRequestIdRef.current) {
          setIsLoading(false);
          setIsRefreshing(false);
        }
      }
    },
    [api_url]
  );

  useEffect(() => {
    if (!api_url) {
      return;
    }
    const normalizedFilters = formatFilters(appliedFilters);
    if (areFiltersEqual(lastFetchedFilters, normalizedFilters)) {
      return;
    }

    setLastFetchedFilters(normalizedFilters);
    fetchReportData(normalizedFilters, !reportData);
  }, [api_url, appliedFilters, fetchReportData, lastFetchedFilters, reportData]);

  const onFiltersChange = useCallback((nextFilters) => {
    setDraftFilters((current) => {
      const nextDraft = mergeFilters(current, nextFilters);
      setAppliedFilters(nextDraft);
      return nextDraft;
    });
  }, []);

  const onRetry = useCallback(() => {
    fetchReportData(appliedFilters);
  }, [fetchReportData, appliedFilters]);

  const handleExportOpenText = useCallback(async () => {
    if (!export_open_text_url) return;
    setIsExporting(true);
    try {
      const minimumDelay = new Promise((resolve) => setTimeout(resolve, 1000));
      const [blob] = await Promise.all([exportPulseOpenTextCsv(export_open_text_url, appliedFilters), minimumDelay]);
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'pulse_open_responses.csv';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (_exportError) {
      // Silently fail — the user sees the button revert to idle
    } finally {
      setIsExporting(false);
    }
  }, [export_open_text_url, appliedFilters]);

  useEffect(() => {
    const updateStickyOffset = () => {
      setStickyTopOffset(getStickyTopOffset());
    };

    updateStickyOffset();
    window.addEventListener('resize', updateStickyOffset);

    return () => {
      window.removeEventListener('resize', updateStickyOffset);
    };
  }, []);

  useEffect(() => {
    const sentinel = filterBarSentinelRef.current;
    if (!sentinel) return undefined;
    const observer = new IntersectionObserver(([entry]) => setIsFilterBarStuck(!entry.isIntersecting), {
      threshold: 0,
      rootMargin: `-${stickyTopOffset + 1}px 0px 0px 0px`,
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [stickyTopOffset]);

  const surveys = reportData?.surveys || [];
  const selectedSurveyId = draftFilters.survey_id ? `${draftFilters.survey_id}` : '';
  const selectedSurvey = surveys.find((survey) => survey.id.toString() === selectedSurveyId);
  const selectedSurveyLabel = selectedSurvey?.name || 'Select Survey Period';

  return (
    <Box sx={{ pb: 5, mx: 'auto', maxWidth: 1320 }}>
      {isLoading && !reportData && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
          <Box sx={{ mt: 1 }}>
            <Button color="inherit" size="small" variant="outlined" onClick={onRetry}>
              Retry
            </Button>
          </Box>
        </Alert>
      )}

      {isRefreshing && (
        <Box sx={{ position: 'fixed', top: 12, right: 12 }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {reportData && (
        <Box sx={{ px: { xs: 0, md: 1 } }}>
          <Box
            sx={{
              position: 'relative',
              mb: 1.1,
              pb: 1.3,
              '&::after': {
                content: '""',
                position: 'absolute',
                left: '50%',
                bottom: 0,
                width: '100vw',
                transform: 'translateX(-50%)',
                borderBottom: '1px solid #d9e2ee',
                pointerEvents: 'none',
              },
            }}
          >
            <Link
              href={links.data_analysis}
              underline="none"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.65,
                mb: 1.0,
                color: '#64748b',
                fontSize: '0.9rem',
                '&:hover': {
                  color: '#334155',
                  textDecoration: 'none',
                },
              }}
            >
              <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: '0.78rem' }} />
              Back to Data Analysis
            </Link>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', md: 'center' },
                flexDirection: { xs: 'column', md: 'row' },
                gap: 1.1,
              }}
            >
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f2748' }}>
                Pulse Dashboard
              </Typography>
              <FormControl
                size="small"
                sx={{
                  width: '100%',
                  maxWidth: { xs: '100%', md: 360 },
                  flex: { xs: '1 1 100%', md: '0 1 360px' },
                  minWidth: 0,
                  ...surveyPeriodSelectStyles,
                }}
              >
                <Select
                  value={selectedSurveyId}
                  onChange={(event) =>
                    onFiltersChange({
                      survey_id: event.target.value,
                    })
                  }
                  displayEmpty
                  aria-label="Survey Period"
                  renderValue={() => selectedSurveyLabel}
                  variant="standard"
                  input={<Input disableUnderline />}
                  IconComponent={SelectChevronIcon}
                >
                  {surveys.map((survey) => (
                    <MenuItem key={survey.id} value={survey.id.toString()}>
                      {survey.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Box ref={filterBarSentinelRef} sx={{ height: 0, visibility: 'hidden' }} />
          <Box
            sx={{
              position: 'sticky',
              top: `${stickyTopOffset}px`,
              zIndex: 1000,
              backgroundColor: '#ffffff',
              pt: 0.9,
              pb: 1.35,
              mb: 2.3,
              borderBottom: `1px solid ${isFilterBarStuck ? '#e2e8f0' : 'transparent'}`,
              transition: 'border-color 0.15s ease',
            }}
          >
            <FilterBarBespoke2026
              surveysFilters={reportData.available_filters || { schools: [], roles: [] }}
              filters={draftFilters}
              onChange={onFiltersChange}
              sortOrder={sortOrder}
              onSortOrderChange={setSortOrder}
              isSuperadmin={isSuperadmin}
              themeKey={themeKey}
              onThemeKeyChange={setThemeKey}
            />
          </Box>
          <LikertSection
            sections={sections.likert}
            groupedSection={sections.likert_grouped}
            groupBy={draftFilters.group_by}
            viewMode={likertViewMode}
            onViewModeChange={setLikertViewMode}
            sortOrder={sortOrder}
            themeKey={themeKey}
          />
          <OpenTextSection sections={sections.open_text} onExport={handleExportOpenText} isExporting={isExporting} />
        </Box>
      )}
    </Box>
  );
};

PulseAnalysisV2Container.propTypes = {
  api_url: PropTypes.string.isRequired,
  export_open_text_url: PropTypes.string,
  links: PropTypes.object.isRequired,
  permission_props: PropTypes.object.isRequired,
  initial_report_data: PropTypes.object,
  report_filters: PropTypes.shape({
    survey_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    school: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    role: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    group_by: PropTypes.oneOf(['none', 'school', 'role']),
  }),
};

PulseAnalysisV2Container.defaultProps = {
  export_open_text_url: null,
  report_filters: {},
  initial_report_data: null,
};

export default (props, railsContext) => {
  return () => (
    <Layout railsContext={railsContext}>
      <ThemeProvider theme={themeAttuned}>
        <ConfirmDialogProvider>
          <PulseAnalysisV2Container {...props} />
        </ConfirmDialogProvider>
      </ThemeProvider>
    </Layout>
  );
};
